import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { updateUtilityService } from '../../api/utilityServices';
import { getAllLookups } from '../../api/lookups';
import { UtilityService, UtilityType } from '../../types/utility.types';
import { Lookup } from '../../types/lookup.types';
import { displayError } from '../../utils/errorHandler';
import { validateUtilityService } from '../../utils/validation';

// Helper function to parse number inputs safely
const parseNumericInput = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
};

// Helper function to format number for display in text field
const formatNumberForInput = (value: number | undefined): string => {
  if (value === undefined || value === null) return '';
  return value.toString();
};

interface EditUtilityServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  service: UtilityService;
  propertyId: string;
  clientId: string;
}

const utilityTypes: UtilityType[] = [
  'Gas',
  'Electricity',
  'Water',
  'Telecoms',
  'Waste'
];

// Status options will be fetched from lookups API

const EditUtilityServiceDialog: React.FC<EditUtilityServiceDialogProps> = ({
  open,
  onClose,
  onEdit,
  service,
  propertyId,
  clientId
}) => {
  const [formData, setFormData] = useState({
    serviceType: service.serviceType,
    accountNumber: service.accountNumber || '',
    status: service.status,
    contractStartDate: service.contractStartDate ? new Date(service.contractStartDate) : null,
    contractEndDate: service.contractEndDate ? new Date(service.contractEndDate) : null,
    notes: service.notes || '',
    supplier: service.supplier || '',
    currentRate: service.currentRate || '',
    eacAq: service.eacAq || '',
    client: service.client,
    property: service.property,
    meterDetails: service.meterDetails || {
      meterType: '',
      location: '',
      serialNumber: '',
      mpanMprnSpid: ''
    },
    _id: service._id,
    createdAt: service.createdAt,
    updatedAt: service.updatedAt,
    // New numeric fields
    dayRate: service.dayRate,
    dayConsumption: service.dayConsumption,
    nightRate: service.nightRate,
    nightConsumption: service.nightConsumption,
    weekendRate: service.weekendRate,
    weekendConsumption: service.weekendConsumption,
    standingCharge: service.standingCharge,
    uplift: service.uplift
  });
  const [error, setError] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  // Fetch status options from lookups API
  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const lookups = await getAllLookups();
        const statusLookups = lookups.filter((lookup: Lookup) => 
          lookup.type === 'UtilityServiceStatus' && lookup.active
        );
        setStatusOptions(statusLookups.map((lookup: Lookup) => lookup.value));
      } catch (error) {
        console.error('Failed to fetch status options:', error);
        displayError(error, 'Failed to load status options');
        // Fallback to basic options if API fails
        setStatusOptions(['LIVE', 'PENDING', 'TERMINATED']);
      }
    };
    
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    setFormData({
      serviceType: service.serviceType || '',
      accountNumber: service.accountNumber || '',
      status: service.status,
      contractStartDate: service.contractStartDate ? new Date(service.contractStartDate) : null,
      contractEndDate: service.contractEndDate ? new Date(service.contractEndDate) : null,
      notes: service.notes || '',
      supplier: service.supplier || '',
      currentRate: service.currentRate || '',
      eacAq: service.eacAq || '',
      client: service.client,
      property: service.property,
      meterDetails: service.meterDetails || {
        meterType: '',
        location: '',
        serialNumber: '',
        mpanMprnSpid: ''
      },
      _id: service._id,
      createdAt: service.createdAt,
      updatedAt: service.updatedAt,
      // New numeric fields
      dayRate: service.dayRate,
      dayConsumption: service.dayConsumption,
      nightRate: service.nightRate,
      nightConsumption: service.nightConsumption,
      weekendRate: service.weekendRate,
      weekendConsumption: service.weekendConsumption,
      standingCharge: service.standingCharge,
      uplift: service.uplift
    });
  }, [service]);

  const handleNestedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const [parent, child] = name.split('.');
    
    setFormData((prev) => {
      // Create a safe copy of the nested object with proper type handling
      // Use type assertion to tell TypeScript this is a Record<string, any>
      const parentObj = (prev[parent as keyof typeof prev] || {}) as Record<string, any>;
      
      return {
        ...prev,
        [parent]: {
          ...parentObj,
          [child]: value
        }
      };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Handle numeric fields
    if (['dayRate', 'dayConsumption', 'nightRate', 'nightConsumption', 'weekendRate', 'weekendConsumption', 'standingCharge', 'uplift'].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        [name]: parseNumericInput(value)
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Client-side validation
      const validationResult = validateUtilityService({
        serviceType: formData.serviceType,
        supplier: typeof formData.supplier === 'object' && formData.supplier !== null && '_id' in formData.supplier
          ? (formData.supplier as any)._id
          : typeof formData.supplier === 'string'
          ? formData.supplier
          : undefined,
        status: formData.status,
        meterDetails: formData.meterDetails,
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,
        dayRate: formData.dayRate,
        nightRate: formData.nightRate,
        standingCharge: formData.standingCharge,
        dayConsumption: formData.dayConsumption,
        nightConsumption: formData.nightConsumption
      });

      if (!validationResult.isValid) {
        // Display first error
        const firstError = Object.values(validationResult.errors)[0];
        throw new Error(firstError);
      }

      // Send all form data without filtering
      const formattedData = {
        ...formData,
        contractStartDate: formData.contractStartDate ? formData.contractStartDate.toISOString().split('T')[0] : '',
        contractEndDate: formData.contractEndDate ? formData.contractEndDate.toISOString().split('T')[0] : ''
      };
      await updateUtilityService(service._id, formattedData);
      onEdit();
      onClose();
    } catch (err: any) {
      const parsed = displayError(err, 'Failed to update utility service');
      setError(parsed.message);
    }
  };

  // Check if service is energy type (gas or electricity)
  const isEnergyService = service.serviceType === 'Gas' || service.serviceType === 'Electricity';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(25, 25, 25, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white',
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ color: 'white' }}>Edit Utility Service</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3, color: 'white' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Service Type"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleSelectChange}
                required
              >
                {utilityTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleSelectChange}
                required
              >
                {statusOptions.map((status: string) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Contract Start Date"
                  value={formData.contractStartDate}
                  onChange={(date) => {
                    setFormData(prev => ({
                      ...prev,
                      contractStartDate: date
                    }));
                  }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.87)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(0, 0, 0, 0.6)',
                        },
                        '& .MuiInputBase-input': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Contract End Date"
                  value={formData.contractEndDate}
                  onChange={(date) => {
                    setFormData(prev => ({
                      ...prev,
                      contractEndDate: date
                    }));
                  }}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.87)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(0, 0, 0, 0.6)',
                        },
                        '& .MuiInputBase-input': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Current Rate"
                name="currentRate"
                value={formData.currentRate}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="EAC/AQ"
                name="eacAq"
                value={formData.eacAq}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Only show tariff breakdown for energy services */}
            {isEnergyService && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Tariff Breakdown</Typography>
                </Grid>
                
                {/* Standing Charge */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Standing Charge (p/day)"
                    name="standingCharge"
                    value={formatNumberForInput(formData.standingCharge)}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ step: '0.01' }}
                    helperText="Daily standing charge in pence"
                  />
                </Grid>
                
                {/* Uplift */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Uplift (p)"
                    name="uplift"
                    value={formatNumberForInput(formData.uplift)}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ step: '0.01' }}
                    helperText="Commission uplift in pence"
                  />
                </Grid>
                
                {/* Day Rate & Consumption */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Day Rate (p/kWh)"
                    name="dayRate"
                    value={formatNumberForInput(formData.dayRate)}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ step: '0.0001' }}
                    helperText="Day unit rate in pence per kWh"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Day Consumption (kWh)"
                    name="dayConsumption"
                    value={formatNumberForInput(formData.dayConsumption)}
                    onChange={handleChange}
                    type="number"
                    helperText="Day consumption in kilowatt-hours"
                  />
                </Grid>
                
                {/* Night Rate & Consumption */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Night Rate (p/kWh)"
                    name="nightRate"
                    value={formatNumberForInput(formData.nightRate)}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ step: '0.0001' }}
                    helperText="Night unit rate in pence per kWh"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Night Consumption (kWh)"
                    name="nightConsumption"
                    value={formatNumberForInput(formData.nightConsumption)}
                    onChange={handleChange}
                    type="number"
                    helperText="Night consumption in kilowatt-hours"
                  />
                </Grid>
                
                {/* Weekend Rate & Consumption */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Weekend Rate (p/kWh)"
                    name="weekendRate"
                    value={formatNumberForInput(formData.weekendRate)}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ step: '0.0001' }}
                    helperText="Weekend unit rate in pence per kWh"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Weekend Consumption (kWh)"
                    name="weekendConsumption"
                    value={formatNumberForInput(formData.weekendConsumption)}
                    onChange={handleChange}
                    type="number"
                    helperText="Weekend consumption in kilowatt-hours"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Meter Details</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meter Serial Number"
                name="meterDetails.serialNumber"
                value={formData.meterDetails?.serialNumber || ''}
                onChange={handleNestedChange}
                helperText="Physical meter serial number"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={formData.serviceType === 'Gas' ? 'MPRN' : 
                      formData.serviceType === 'Electricity' ? 'MPAN' : 
                      formData.serviceType === 'Water' ? 'SPID' : 'Meter Identifier'}
                name="meterDetails.mpanMprnSpid"
                value={formData.meterDetails?.mpanMprnSpid || ''}
                onChange={handleNestedChange}
                helperText={formData.serviceType === 'Gas' ? 'Meter Point Reference Number' : 
                         formData.serviceType === 'Electricity' ? 'Meter Point Administration Number' : 
                         formData.serviceType === 'Water' ? 'Supply Point ID' : 'Meter Identifier'}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meter Type"
                name="meterDetails.meterType"
                value={formData.meterDetails?.meterType || ''}
                onChange={handleNestedChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meter Location"
                name="meterDetails.location"
                value={formData.meterDetails?.location || ''}
                onChange={handleNestedChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={onClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.serviceType || !formData.supplier}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              backdropFilter: 'blur(4px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Update Service
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUtilityServiceDialog;
